import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPITyped,
} from './api-requests';

/**
 * Tutorial status response
 */
export interface TutorialStatus {
  hostRevenueSplitTutorial: boolean;
  partnerSplitTutorial: boolean;
}

/**
 * Tutorial type for marking complete
 */
export type TutorialType = 'HOST_REVENUE_SPLIT' | 'PARTNER_SPLIT';

/**
 * Response from marking tutorial complete
 */
export interface MarkTutorialCompleteResponse {
  message: string;
  completed: boolean;
  tutorialType: TutorialType;
}

/**
 * Get the current user's tutorial completion status
 * 
 * @returns Promise<ApiResponse<TutorialStatus | null>>
 * 
 * @example
 * ```typescript
 * const response = await getTutorialStatus();
 * if (isSuccessful(response.status)) {
 *   const { hostRevenueSplitTutorial, partnerSplitTutorial } = response.payload;
 *   if (!hostRevenueSplitTutorial) {
 *     showHostTutorial();
 *   }
 * }
 * ```
 */
export const getTutorialStatus = async (): Promise<
  ApiResponse<TutorialStatus | null>
> => {
  const request: ApiRequest = {
    route: 'user/tutorial-status',
    request: {
      method: 'GET',
      entity: 'user',
      body: undefined,
    },
  };

  return fetchDataFromAPITyped<TutorialStatus>(request);
};

/**
 * Mark a tutorial as complete for the current user
 * 
 * @param tutorialType - Type of tutorial to mark complete
 * @returns Promise<ApiResponse<MarkTutorialCompleteResponse | null>>
 * 
 * @example
 * ```typescript
 * const response = await markTutorialComplete('HOST_REVENUE_SPLIT');
 * if (isSuccessful(response.status)) {
 *   console.log('Tutorial marked complete:', response.payload?.message);
 * }
 * ```
 */
export const markTutorialComplete = async (
  tutorialType: TutorialType
): Promise<ApiResponse<MarkTutorialCompleteResponse | null>> => {
  const request: ApiRequest = {
    route: 'user/tutorial-complete',
    request: {
      method: 'POST',
      entity: 'user',
      body: {
        tutorialType,
      },
    },
  };

  return fetchDataFromAPITyped<MarkTutorialCompleteResponse>(request);
};

